import { useState } from 'react';
import { AxiosRequestConfig } from 'axios';

import { Col, Row, Button } from 'darrius';

import { axiosInstance } from '../../../utils/axiosInstance';
import { triggerEvent } from '../../../utils/triggerEvent';

import { showToast } from '../../../components/atoms/Toast/Toast';
import { Dialog } from '../../../components';

import { IPurchaseFixedPriceServiceResponse } from '../../../pages/api/purchase/fixed-price-service';
import { IUserTransformationReturn } from '../../../modules/profile/shared/transformations/user/@interfaces';

import styles from './ProfileInstantMatchButtons.module.scss';

interface IProfileInstantMatchButtonsProps {
  profileInstantMatchButtonsData: {
    requestId: string;
    user: IUserTransformationReturn;
    isUserCliAuthenticated: boolean;
    isSentMessage: boolean;
  };
  isSentMessageSuccess: boolean;
  slug: string;
}

export function ProfileInstantMatchButtons({
  profileInstantMatchButtonsData,
  isSentMessageSuccess,
  slug,
}: IProfileInstantMatchButtonsProps) {
  const { isSentMessage, isUserCliAuthenticated, requestId, user } =
    profileInstantMatchButtonsData;

  const [isHiringServiceLoading, setIsHiringServiceLoading] = useState(false);

  const TRACK_EVENT_NAME = 'instant_contact_cli';
  const TRACK_ACTION = 'click';
  const TRACK_PLATFORM = 'web';

  function trackProfileButtons(label: string, eventName: string) {
    triggerEvent(eventName, {
      requestId: user.requestId,
      profileId: user.id,
      label: label,
      action: TRACK_ACTION,
      platform: TRACK_PLATFORM,
      value: user.partner.price ? user.partner.price : null,
    });
  }

  async function handleHireService() {
    try {
      trackProfileButtons('checkout', TRACK_EVENT_NAME);

      setIsHiringServiceLoading(true);

      const requestOptions: AxiosRequestConfig = {
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/fixed-price-service`,
        method: 'POST',
        data: {
          profileId: user.id,
          requestId: user.requestId,
        },
      };

      const { data } =
        await axiosInstance<IPurchaseFixedPriceServiceResponse>(requestOptions);

      if (data.status && data.status === 'error' && data.message) {
        showToast({ message: data.message, type: 'error' });

        setIsHiringServiceLoading(false);
        return;
      }

      if (data.url) {
        window.location.href = data.url;
        return;
      }

      showToast({
        message: 'Ocorreu um erro ao tentar contratar o serviço',
        type: 'error',
      });

      setIsHiringServiceLoading(false);
    } catch (error: any) {
      showToast({
        message: error.response?.data?.message || 'Erro desconhecido',
        type: 'error',
      });
      setIsHiringServiceLoading(false);
    }
  }

  function renderProfileCTAButtons() {
    if (isUserCliAuthenticated) {
      const proIsPartner = user.partner?.isPartnerProfile;
      const proHasPrice = !!user.partner?.price;
      const proIsPartnerWithPrice = proIsPartner && proHasPrice;

      if (proIsPartnerWithPrice) {
        return (
          <div className="no-margins">
            <Row
              className={`${styles['profile-instant-match-buttons']} no-margins`}
            >
              <Col className="buttons">
                <div className={styles['profile-instant-match-buttons__chat']}>
                  <Dialog.Trigger asChild>
                    <Button.Root
                      disabled={
                        isHiringServiceLoading ||
                        isSentMessage ||
                        isSentMessageSuccess
                      }
                      variation="secondary"
                      onClick={() =>
                        trackProfileButtons('go_to_chat', TRACK_EVENT_NAME)
                      }
                    >
                      <Button.Icon name="MdOutlineChat" size={18} />
                    </Button.Root>
                  </Dialog.Trigger>
                </div>
              </Col>
              {!!user.partner.prices && user.partner.prices.length ? (
                <Col className="col-12">
                  <Button.Link
                    href={`/contato-instantaneo/perfil-pro/${user.id}/${slug}/${requestId}/anuncios`}
                    disabled={isHiringServiceLoading}
                    fullWidth
                    onClick={() =>
                      trackProfileButtons('choose_service', TRACK_EVENT_NAME)
                    }
                  >
                    <span className={styles.hireService__text}>
                      Escolher Serviços
                    </span>
                  </Button.Link>
                </Col>
              ) : (
                <Col className="col-12">
                  <Button.Root
                    onClick={handleHireService}
                    isLoading={isHiringServiceLoading}
                    disabled={isHiringServiceLoading}
                    fullWidth
                  >
                    <span className={styles.hireService__text}>
                      Contratar Serviço
                    </span>
                  </Button.Root>
                </Col>
              )}
            </Row>
          </div>
        );
      }

      return (
        <Row>
          <Col className="col-sm-12 col-md-10 col-lg-6 no-margins">
            <Row>
              <Col className="col-sm-12 buttons space-bottom">
                <div className="teste">
                  <Dialog.Trigger asChild>
                    <Button.Root
                      disabled={isSentMessage || isSentMessageSuccess}
                      onClick={() =>
                        trackProfileButtons('enviar_mensagem', TRACK_EVENT_NAME)
                      }
                      variation="primary"
                      fullWidth
                    >
                      <Button.Icon name="MdOutlineChat" size={18} />
                      <span>Enviar mensagem</span>
                    </Button.Root>
                  </Dialog.Trigger>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      );
    }

    return null;
  }

  return <>{renderProfileCTAButtons()}</>;
}
